import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ChangeEvent,
  Dispatch,
  KeyboardEvent,
  RefObject,
  SetStateAction,
} from "react";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { normalizeText } from "utils/text.normalize.logic";
import {
  getSearchableDropdownKeyboardAction,
  type SearchableDropdownKeyboardAction,
} from "./searchableDropdown.keyboard.logic";
import type {
  SearchableDropdownOption,
  SearchableDropdownProps,
  SearchableDropdownState,
} from "./searchableDropdown.types";
import {
  buildMultiSelectDisplay,
  findSelectedLabel,
} from "./searchableDropdown.utils";

export interface UseSearchableDropdownResult {
  readonly baseId: string;
  readonly canClear: boolean;
  readonly containerRef: RefObject<HTMLDivElement>;
  readonly filteredOptions: readonly SearchableDropdownOption[];
  readonly highlightedIndex: number;
  readonly inputRef: RefObject<HTMLInputElement>;
  readonly isFilled: boolean;
  readonly isOpen: boolean;
  readonly labelId: string;
  readonly listboxId: string;
  readonly listRef: RefObject<HTMLUListElement>;
  readonly multiSelectDisplay: {
    readonly displayLabel: string;
    readonly extraCount: number;
    readonly fullLabel: string;
    readonly labels: string[];
  };
  readonly placeholderText: string;
  readonly query: string;
  readonly resolvedSelectedValues: readonly string[];
  readonly resolvedValue: string;
  readonly selectedLabel: string;
  readonly selectedValueSet: ReadonlySet<string>;
  readonly showExtraCountBadge: boolean;
  readonly size: "default" | "compact";
  readonly visualState: SearchableDropdownState;
  readonly clearHighlight: () => void;
  readonly closeDropdown: () => void;
  readonly handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  readonly handleMouseHighlight: (index: number) => void;
  readonly handleSelect: (option: SearchableDropdownOption) => void;
  readonly openDropdown: () => void;
  readonly setQuery: Dispatch<SetStateAction<string>>;
}

interface ApplyKeyboardActionInput {
  readonly action: SearchableDropdownKeyboardAction;
  readonly event: KeyboardEvent<HTMLInputElement>;
}

type HighlightSource = "keyboard" | "mouse";

let searchableDropdownId = 0;

export const useSearchableDropdown = (
  props: SearchableDropdownProps,
  placeholderText: string,
): UseSearchableDropdownResult => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const idRef = useRef<string>();
  const highlightSourceRef = useRef<HighlightSource | null>(null);
  if (!idRef.current) {
    searchableDropdownId += 1;
    idRef.current = `searchable-dropdown-${searchableDropdownId}`;
  }
  const baseId = idRef.current;
  const listboxId = `${baseId}-listbox`;
  const labelId = `${baseId}-label`;

  const size = props.size ?? "default";

  const resolvedSelectedValues = useMemo(
    () => props.selectedValues ?? [],
    [props.selectedValues],
  );

  const isFilled = props.multiSelect
    ? resolvedSelectedValues.length > 0
    : Boolean(props.value);
  const multiSelectDisplay = useMemo(
    () => buildMultiSelectDisplay(props.options, resolvedSelectedValues),
    [props.options, resolvedSelectedValues],
  );
  const selectedLabel = useMemo(() => {
    if (props.multiSelect) {
      return multiSelectDisplay.displayLabel;
    }
    return findSelectedLabel(props.options, props.value);
  }, [
    props.multiSelect,
    multiSelectDisplay.displayLabel,
    props.options,
    props.value,
  ]);
  const showExtraCountBadge =
    !isOpen && Boolean(props.multiSelect) && multiSelectDisplay.extraCount > 0;
  const canClear = Boolean(props.onClear) && isFilled && !props.disabled;

  const selectedValueSet = useMemo(
    () => new Set(resolvedSelectedValues),
    [resolvedSelectedValues],
  );

  const visualState = useMemo<SearchableDropdownState>(() => {
    if (props.disabled) return "disabled";
    if (props.hasError) return "error";
    if (isOpen) return "active";
    if (isFilled) return "filled";
    return "inactive";
  }, [props.disabled, props.hasError, isOpen, isFilled]);

  const normalizedOptions = useMemo(
    () =>
      props.options.map((option) => ({
        option,
        normalizedLabel: normalizeText(option.label),
      })),
    [props.options],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) return props.options;
    return normalizedOptions
      .filter((entry) => entry.normalizedLabel.includes(normalizedQuery))
      .map((entry) => entry.option);
  }, [normalizedOptions, props.options, query]);

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      highlightSourceRef.current = null;
      setHighlightedIndex(-1);
    }
  }, [filteredOptions.length, highlightedIndex]);

  useEffect(() => {
    if (
      !isOpen ||
      highlightedIndex < 0 ||
      highlightSourceRef.current !== "keyboard"
    ) {
      return;
    }
    highlightSourceRef.current = null;
    const optionId = `${listboxId}-option-${highlightedIndex}`;
    const target = document.getElementById(optionId);
    if (target) {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, isOpen, listboxId]);

  useEffect(() => {
    if (!props.disabled || !isOpen) return;
    highlightSourceRef.current = null;
    setIsOpen(false);
    setQuery("");
    setHighlightedIndex(-1);
  }, [props.disabled, isOpen]);

  useOnClickOutside(
    [containerRef, listRef],
    () => {
      setIsOpen(false);
      setQuery(selectedLabel);
      props.onBlur?.();
    },
    { enabled: isOpen, eventType: "mousedown" },
  );

  const openDropdown = (): void => {
    if (props.disabled) return;
    highlightSourceRef.current = null;
    setIsOpen(true);
    setQuery("");
    setHighlightedIndex(-1);
    props.onFocus?.();
  };

  const closeDropdown = (): void => {
    highlightSourceRef.current = null;
    setIsOpen(false);
    setQuery("");
    props.onBlur?.();
  };

  const handleSelect = (option: SearchableDropdownOption): void => {
    if (props.disabled) return;
    if (props.multiSelect) {
      const next = new Set(resolvedSelectedValues);
      if (next.has(option.value)) next.delete(option.value);
      else next.add(option.value);
      props.onSelectedValuesChange?.(Array.from(next));
      setQuery("");
      return;
    }
    props.onChange?.(option.value);
    setQuery(option.label);
    setIsOpen(false);
    props.onBlur?.();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (props.disabled) return;
    highlightSourceRef.current = null;
    if (!isOpen) setIsOpen(true);
    setQuery(event.target.value);
    setHighlightedIndex(-1);
  };

  const handleMouseHighlight = (index: number): void => {
    highlightSourceRef.current = "mouse";
    setHighlightedIndex(index);
  };

  const clearHighlight = (): void => {
    highlightSourceRef.current = null;
    setHighlightedIndex(-1);
  };

  const applyKeyboardAction = ({
    action,
    event,
  }: ApplyKeyboardActionInput): void => {
    switch (action.type) {
      case "none":
        return;
      case "open":
        event.preventDefault();
        openDropdown();
        return;
      case "prevent-default":
        event.preventDefault();
        return;
      case "highlight":
        event.preventDefault();
        highlightSourceRef.current = "keyboard";
        setHighlightedIndex(action.index);
        return;
      case "select":
        if (action.shouldPreventDefault) event.preventDefault();
        handleSelect(action.option);
        return;
      case "close":
        if (action.shouldPreventDefault) event.preventDefault();
        closeDropdown();
        return;
      default: {
        const exhaustive: never = action;
        return exhaustive;
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (props.disabled) return;
    const action = getSearchableDropdownKeyboardAction({
      filteredOptions,
      highlightedIndex,
      isOpen,
      key: event.key,
      query,
      shouldSelectOnEnterOrTab: Boolean(props.shouldSelectOnEnterOrTab),
    });
    applyKeyboardAction({ action, event });
  };

  const resolvedValue = isOpen ? query : selectedLabel;

  return {
    baseId,
    canClear,
    containerRef,
    filteredOptions,
    highlightedIndex,
    inputRef,
    isFilled,
    isOpen,
    labelId,
    listboxId,
    listRef,
    multiSelectDisplay,
    placeholderText,
    query,
    resolvedSelectedValues,
    resolvedValue,
    selectedLabel,
    selectedValueSet,
    showExtraCountBadge,
    size,
    visualState,
    clearHighlight,
    closeDropdown,
    handleInputChange,
    handleKeyDown,
    handleMouseHighlight,
    handleSelect,
    openDropdown,
    setQuery,
  };
};
